import { useState, useCallback, useEffect, useRef } from "react";
import {
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth.tsx";
import { UrqlProvider } from "./api/provider.tsx";
import {
  ErrorBoundary,
  ScheduleErrorBoundary,
  ProfileErrorBoundary,
  ChurchErrorBoundary,
  PrayersErrorBoundary,
  WorshipErrorBoundary,
  HomeGroupErrorBoundary,
  EventsErrorBoundary,
  OrdersErrorBoundary,
  PaymentErrorBoundary,
  TicketsErrorBoundary,
} from "./components/ErrorBoundary.tsx";
import { storageLog } from "./lib/logger.ts";
import { IntroAnimation } from "./components/IntroAnimation.tsx";
import { PageLoader, SplashScreen } from "./components/PageLoader.tsx";
import {
  HomePage,
  FeedbackPage,
  PastorPage,
  ChurchPage,
  WelcomePage,
} from "./features/home/index.ts";
import { HomeGroupsPage, HomeGroupPage } from "./features/home-groups/index.ts";
// Academy feature
import { AcademyPage, CoursePage } from "./features/academy/index.ts";
// Admin Academy feature
import {
  AdminCoursesPage,
  AdminCourseEditPage,
  CourseAnalyticsPage,
} from "./features/admin/academy/index.ts";
import { SchedulePage } from "./pages/profile/SchedulePage.tsx";
import { EventPage } from "./pages/profile/EventPage.tsx";
import { OrderPage } from "./pages/profile/OrderPage.tsx";
import { OrdersAndTicketsPage } from "./pages/profile/OrdersAndTicketsPage.tsx";
import { PaymentPage } from "./pages/profile/PaymentPage.tsx";
import { PaymentThanksPage } from "./pages/profile/PaymentThanksPage.tsx";
import { TicketPage } from "./pages/profile/TicketPage.tsx";
// Admin pages - new feature-based structure
import { AdminHomePage } from "./features/admin/home/AdminHomePage.tsx";
import {
  AdminOrdersPage,
  EventOrdersPage,
} from "./features/admin/orders/index.ts";
import {
  TicketValidationPage,
  AdminTicketViewPage,
  AdminCashTicketsPage,
  AdminTicketEventsPage,
} from "./features/admin/tickets/index.ts";
// Legacy admin pages (still in pages/admin)
import { AdminPaymentsPage } from "./pages/admin/AdminPaymentsPage.tsx";
import { AdminCategoriesPage } from "./pages/admin/AdminCategoriesPage.tsx";
import { AdminCategoryEditPage } from "./pages/admin/AdminCategoryEditPage.tsx";
// Events feature
import {
  AdminEventsPage,
  AdminEventEditPage,
} from "./features/admin/events/index.ts";
// Home groups feature
import {
  AdminHomeGroupsPage,
  AdminHomeGroupEditPage,
} from "./features/admin/home-groups/index.ts";
// Ministries feature
import {
  AdminMinistriesPage,
  AdminMinistryEditPage,
} from "./features/admin/ministries/index.ts";
// Raffle feature
import {
  AdminRaffleEventsPage,
  AdminRafflePage,
  AdminRaffleEditPage,
} from "./features/admin/raffle/index.ts";
import {
  RaffleScreen,
  RaffleViewerPage,
} from "./features/raffle/index.ts";
// Gender Party feature
import {
  GenderPartyScreen,
  GenderPartyViewerPage,
} from "./features/gender-party/index.ts";
import {
  AdminGenderPartyListPage,
  AdminGenderPartyEditPage,
} from "./features/admin/gender-party/index.ts";
// Content feature
import {
  AdminContentPage,
  AdminFeedbackPage,
  AdminPastorPage,
  AdminChurchPage,
  AdminHomeGroupsPageConfig,
  AdminWelcomePage,
} from "./features/admin/content/index.ts";
// Access management feature
import { AdminAccessPage } from "./features/admin/access/index.ts";
import {
  AdminPanelRoute,
  AdminOperatorRoute,
  AdminAdminRoute,
  AdminRaffleRoute,
  AdminTicketRoute,
  AdminTicketIssueRoute,
  AdminGenderPartyRoute,
  AdminAcademyRoute,
} from "./components/AdminRouteGuard.tsx";
import { ProfilePage } from "./pages/profile/ProfilePage.tsx";
import { ProfileEmailPage } from "./pages/profile/ProfileEmailPage.tsx";
import { ProfilePasswordPage } from "./pages/profile/ProfilePasswordPage.tsx";
import { ProfileSettingsPage } from "./pages/profile/ProfileSettingsPage.tsx";
import { NotFoundPage } from "./pages/profile/NotFoundPage.tsx";
import { LoginPage } from "./pages/profile/LoginPage.tsx";
import { SoonPage } from "./pages/profile/SoonPage.tsx";
import { PrayersPage } from "./pages/profile/PrayersPage.tsx";
import { NeedPrayerPage } from "./pages/profile/NeedPrayerPage.tsx";
import { DonationsPage } from "./pages/profile/DonationsPage.tsx";
import { LeadershipSchoolPage } from "./pages/profile/LeadershipSchoolPage.tsx";
import { StartCoursePage } from "./pages/profile/StartCoursePage.tsx";
import { FamilyClubPage } from "./pages/profile/FamilyClubPage.tsx";
import { MasterClassesPage } from "./pages/profile/MasterClassesPage.tsx";
import { WomensBreakfastsPage } from "./pages/profile/WomensBreakfastsPage.tsx";
import { YouthPage } from "./pages/profile/YouthPage.tsx";
import { BusinessClubPage } from "./pages/profile/BusinessClubPage.tsx";
import { WorshipPage } from "./pages/profile/WorshipPage.tsx";
import { ChooseRolePage } from "./pages/profile/ChooseRolePage.tsx";
import { InstallInstructionPage } from "./pages/profile/InstallInstructionPage.tsx";
import { MagicLinkPage } from "./pages/profile/MagicLinkPage.tsx";
import { shouldPersistSession } from "./lib/pwa.ts";
import { getStoredTokens } from "./lib/storage.ts";
import { isTelegramMiniApp, getTelegram } from "./lib/telegram.ts";

const INTRO_SHOWN_KEY = "app_intro_shown";

/** Paths that are public in browser/PWA (no login required). */
const PUBLIC_PATHS = ["/", "/install", "/auth/magic", "/welcome"];

/**
 * Hide the inline HTML splash overlay (shown immediately on iOS PWA reload).
 * Called when React is ready to take over rendering.
 */
function hideHtmlSplash(): void {
  const splash = document.getElementById("app-splash");
  if (!splash) return;
  // Fade out
  splash.style.opacity = "0";
  // Remove from DOM after transition
  setTimeout(() => {
    splash.remove();
  }, 200);
}

/** localStorage keys for route/scroll restore after reload (PWA / Mini App). */
const LAST_ROUTE_KEY = "one-church:last_route";
const LAST_SCROLL_KEY = "one-church:last_scroll";

/** Maps start_param from Telegram deep link to app path */
const DEEP_LINK_START_PARAM_TO_PATH: Record<string, string> = {
  schedule: "/schedule",
  "home-groups": "/home-groups",
  welcome: "/welcome",
  academy: "/academy",
};

/** Prefix for event deep links: startapp=event_<eventId> */
const EVENT_START_PARAM_PREFIX = "event_";
/** Prefix for order deep links: startapp=order_<orderId> */
const ORDER_START_PARAM_PREFIX = "order_";
/** Prefix for raffle deep links: startapp=raffle_<raffleId> */
const RAFFLE_START_PARAM_PREFIX = "raffle_";
/** Prefix for gender party deep links: startapp=genderparty_<genderPartyId> */
const GENDER_PARTY_START_PARAM_PREFIX = "genderparty_";
/** Prefix for academy course deep links: startapp=academy_<courseId> */
const ACADEMY_COURSE_START_PARAM_PREFIX = "academy_";

/** Saves and restores last route + scroll when shouldPersistSession() (PWA / Mini App). Renders nothing. */
function PersistRouteAndScroll() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoading } = useAuth();
  const restoredRef = useRef(false);

  // Handle Telegram deep link (start_param) — navigate to target page when opening via link.
  // Wait for auth to complete so Layout/TabBar are properly mounted before navigation.
  useEffect(() => {
    if (!isTelegramMiniApp() || restoredRef.current || isLoading) return;
    const startParam = getTelegram()?.initDataUnsafe?.start_param;
    if (!startParam) return;
    let targetPath = DEEP_LINK_START_PARAM_TO_PATH[startParam];
    if (!targetPath && startParam.startsWith(EVENT_START_PARAM_PREFIX)) {
      const eventId = startParam.slice(EVENT_START_PARAM_PREFIX.length);
      if (eventId) targetPath = `/event/${decodeURIComponent(eventId)}`;
    }
    if (!targetPath && startParam.startsWith(ORDER_START_PARAM_PREFIX)) {
      const orderId = startParam.slice(ORDER_START_PARAM_PREFIX.length);
      if (orderId) targetPath = `/order/${decodeURIComponent(orderId)}`;
    }
    if (!targetPath && startParam.startsWith(RAFFLE_START_PARAM_PREFIX)) {
      const raffleId = startParam.slice(RAFFLE_START_PARAM_PREFIX.length);
      if (raffleId) targetPath = `/raffle/${decodeURIComponent(raffleId)}`;
    }
    if (!targetPath && startParam.startsWith(GENDER_PARTY_START_PARAM_PREFIX)) {
      const genderPartyId = startParam.slice(GENDER_PARTY_START_PARAM_PREFIX.length);
      if (genderPartyId) targetPath = `/gender-party/${decodeURIComponent(genderPartyId)}`;
    }
    if (!targetPath && startParam.startsWith(ACADEMY_COURSE_START_PARAM_PREFIX)) {
      const courseId = startParam.slice(ACADEMY_COURSE_START_PARAM_PREFIX.length);
      if (courseId) targetPath = `/academy/${decodeURIComponent(courseId)}`;
    }
    if (targetPath) {
      restoredRef.current = true;
      // Ensure header/main/nav are interactive (they have pointer-events: none until intro-complete)
      const root = document.getElementById("root");
      if (root) {
        root.classList.add("intro-complete", "intro-animated");
        hideHtmlSplash();
      }
      if (location.pathname !== targetPath) {
        navigate(targetPath, { replace: true });
      }
      return;
    }
  }, [location.pathname, navigate, isLoading]);

  // Restore route and scroll once when auth is ready (after full reload)
  // Do not override /install or /auth/* — user explicitly opened these (e.g. "Add to home screen" link)
  useEffect(() => {
    if (!shouldPersistSession() || isLoading || restoredRef.current) return;
    restoredRef.current = true;
    try {
      const current = location.pathname + location.search;
      const isServicePage =
        location.pathname === "/install" ||
        location.pathname.startsWith("/auth/");
      if (isServicePage) {
        // Only restore scroll if any; do not replace route
        const savedScroll = localStorage.getItem(LAST_SCROLL_KEY);
        if (savedScroll !== null) {
          const y = parseInt(savedScroll, 10);
          if (Number.isFinite(y)) {
            requestAnimationFrame(() => window.scrollTo(0, y));
          }
          localStorage.removeItem(LAST_SCROLL_KEY);
        }
        return;
      }
      const savedRoute = localStorage.getItem(LAST_ROUTE_KEY);
      if (savedRoute && savedRoute !== current) {
        navigate(savedRoute, { replace: true });
      }
      const savedScroll = localStorage.getItem(LAST_SCROLL_KEY);
      if (savedScroll !== null) {
        const y = parseInt(savedScroll, 10);
        if (Number.isFinite(y)) {
          requestAnimationFrame(() => {
            window.scrollTo(0, y);
          });
        }
        localStorage.removeItem(LAST_SCROLL_KEY);
      }
    } catch (error) {
      // localStorage may fail in private browsing or when quota exceeded
      storageLog.debug("Failed to restore route/scroll:", error);
    }
  }, [isLoading, location.pathname, location.search, navigate]);

  // Save route on change (exclude service pages that shouldn't be restored)
  useEffect(() => {
    if (!shouldPersistSession()) return;
    const path = location.pathname;
    // Don't save service pages — these shouldn't be restored on next launch
    if (path === "/install" || path.startsWith("/auth/")) return;
    try {
      localStorage.setItem(LAST_ROUTE_KEY, path + location.search);
    } catch (error) {
      // localStorage may fail in private browsing or when quota exceeded
      storageLog.debug("Failed to save route:", error);
    }
  }, [location.pathname, location.search]);

  // Save scroll (debounced)
  useEffect(() => {
    if (!shouldPersistSession()) return;
    let t: ReturnType<typeof setTimeout> | null = null;
    const onScroll = () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => {
        try {
          localStorage.setItem(LAST_SCROLL_KEY, String(window.scrollY));
        } catch {
          // Scroll save failures are non-critical, silently ignore
        }
        t = null;
      }, 150);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (t) clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return null;
}

// Offline screen component
function OfflineScreen({
  retryCount,
  isRetrying,
  onRetry,
}: {
  retryCount: number;
  isRetrying: boolean;
  onRetry: () => void;
}) {
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-amber-50 to-white p-6">
      {/* Heart icon */}
      <div className="mb-6 animate-pulse">
        <svg viewBox="0 0 48 48" className="w-20 h-20">
          <path
            d="M24 42c-1.5-1.5-4-3.5-7-6.5C11 30 4 23 4 15c0-6 5-11 11-11 3.5 0 6.5 1.5 9 4.5 2.5-3 5.5-4.5 9-4.5 6 0 11 5 11 11 0 8-7 15-13 20.5-3 3-5.5 5-7 6.5z"
            fill="#fbbf24"
          />
          <path
            d="M14 14c1.5-2 4-3 6-2"
            stroke="#fef3c7"
            strokeWidth="2"
            strokeLinecap="round"
            fill="none"
            opacity="0.7"
          />
        </svg>
      </div>

      {/* Title */}
      <h1 className="text-xl font-bold text-slate-900 mb-2 text-center">
        Нет подключения к серверу
      </h1>

      {/* Description */}
      <p className="text-slate-600 text-center mb-6 max-w-xs">
        Проверьте подключение к интернету. Мы автоматически попробуем
        подключиться снова.
      </p>

      {/* Retry info */}
      <div className="flex items-center gap-2 text-sm text-slate-500 mb-4 h-5">
        {isRetrying ? (
          <>
            <div className="w-4 h-4 border-2 border-slate-300 border-t-amber-500 rounded-full animate-spin" />
            <span>
              Подключение{retryCount > 0 ? ` (попытка ${retryCount + 1})` : ""}
              ...
            </span>
          </>
        ) : (
          <span>Ожидание следующей попытки...</span>
        )}
      </div>

      {/* Manual retry button */}
      <button
        onClick={onRetry}
        disabled={isRetrying}
        className="px-6 py-3 bg-amber-500 text-white font-medium rounded-xl active:bg-amber-600 transition-colors disabled:opacity-50 disabled:active:bg-amber-500"
      >
        {isRetrying ? "Подключение..." : "Попробовать сейчас"}
      </button>
    </div>
  );
}

// Auth guard with intro animation
function AuthGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const {
    isLoading,
    isAuthenticated,
    isMiniApp,
    account,
    roleChosen,
    setRoleChosen,
    isOffline,
    retryCount,
    retryConnection,
  } = useAuth();

  // Track whether #root classes have been added (prevent duplicate DOM operations)
  const rootClassesAddedRef = useRef(false);
  // Timer ref for intro animation cleanup
  const introAnimationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // When intro was already shown (storage), start with introComplete true to avoid PageLoader flash on reload.
  const [introComplete, setIntroComplete] = useState(() => {
    const storage = shouldPersistSession() ? localStorage : sessionStorage;
    return storage.getItem(INTRO_SHOWN_KEY) === "true";
  });

  const isInstallPage = location.pathname === "/install";

  // Check if intro was already shown. In PWA/Mini App use localStorage so it survives lock/unlock.
  const [isFirstLaunch] = useState(() => {
    const storage = shouldPersistSession() ? localStorage : sessionStorage;
    return storage.getItem(INTRO_SHOWN_KEY) !== "true";
  });

  // Helper to add intro classes to #root (prevents duplicate operations and re-animations)
  const addIntroClasses = useCallback((skipAnimation: boolean) => {
    if (rootClassesAddedRef.current) return;
    const root = document.getElementById("root");
    if (!root) return;

    // Hide inline HTML splash (iOS PWA reload overlay)
    hideHtmlSplash();

    root.classList.add("intro-complete");
    // If skipping animation (returning user, PWA reload), also add intro-animated
    // to prevent CSS animations from playing
    if (skipAnimation) {
      root.classList.add("intro-animated");
    } else {
      // For first launch, add intro-animated after animations complete (~600ms)
      // Clear any previous timer first
      if (introAnimationTimerRef.current) {
        clearTimeout(introAnimationTimerRef.current);
      }
      introAnimationTimerRef.current = setTimeout(() => {
        root.classList.add("intro-animated");
        introAnimationTimerRef.current = null;
      }, 600);
    }
    rootClassesAddedRef.current = true;
  }, []);

  const handleIntroComplete = useCallback(() => {
    setIntroComplete(true);
    const storage = shouldPersistSession() ? localStorage : sessionStorage;
    storage.setItem(INTRO_SHOWN_KEY, "true");
    // First launch: allow animations to play
    addIntroClasses(false);
  }, [addIntroClasses]);

  // Hide HTML splash as soon as React is rendering (even during loading)
  // This prevents "frozen" screen on iOS PWA reload
  useEffect(() => {
    hideHtmlSplash();
  }, []);

  // Cleanup intro animation timer on unmount
  useEffect(() => {
    return () => {
      if (introAnimationTimerRef.current) {
        clearTimeout(introAnimationTimerRef.current);
        introAnimationTimerRef.current = null;
      }
    };
  }, []);

  // CONSOLIDATED EFFECT: Handle all intro completion scenarios
  // - Returning users (intro already shown in storage)
  // - PWA reload with tokens
  // - Screen lock/unlock (bfcache restore)
  // - /install page
  useEffect(() => {
    // /install page: always show content immediately
    if (isInstallPage) {
      addIntroClasses(true);
      return;
    }

    // Already completed intro: ensure classes are present (for PWA reload)
    if (introComplete) {
      addIntroClasses(true);
      return;
    }

    // Returning user (not first launch): skip intro animation
    if (!isFirstLaunch && !isOffline) {
      setIntroComplete(true);
      addIntroClasses(true);
    }
  }, [isInstallPage, introComplete, isFirstLaunch, isOffline, addIntroClasses]);

  // Mini App should never be on /install — redirect to home
  // (InstallInstructionPage also handles this, but this is a safety net)
  if (isMiniApp && isInstallPage) {
    return <Navigate to="/" replace />;
  }

  // /install — публичная страница: сразу инструкция, без интро и без логина
  if (isInstallPage) {
    return (
      <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden">
        {children}
      </div>
    );
  }

  // Show offline screen if API is unreachable (keep showing during retry)
  if (isOffline) {
    return (
      <OfflineScreen
        retryCount={retryCount}
        isRetrying={isLoading}
        onRetry={retryConnection}
      />
    );
  }

  const showIntro = isLoading || !introComplete;

  // Mini App: авторизация сразу (ME_QUERY с initData). Не показываем LoginPage.
  // Browser/PWA: главная и /install публичные; остальные маршруты требуют логин.
  const isPublicPath = PUBLIC_PATHS.includes(location.pathname);
  const needsLogin =
    !isLoading && !isMiniApp && !isAuthenticated && !isPublicPath;
  const content = needsLogin ? <LoginPage /> : children;

  // Сразу после авторизации: пользователь с доступом к админке выбирает «Личный кабинет» или «Админка»
  const canAccessAdmin = account?.canAccessAdmin === true;
  const shouldChooseRole =
    !showIntro && isAuthenticated && canAccessAdmin && !roleChosen;
  const resolvedContent = shouldChooseRole ? (
    <ChooseRolePage standalone onChoose={setRoleChosen} />
  ) : (
    content
  );

  return (
    <>
      <PersistRouteAndScroll />
      {showIntro &&
        (isFirstLaunch ? (
          // First launch in session: full intro animation with sun-heart
          <IntroAnimation
            isLoading={isLoading}
            onComplete={handleIntroComplete}
          />
        ) : shouldPersistSession() && getStoredTokens() ? (
          // PWA/Mini App reload with tokens: minimal splash (no "Подключение..." text)
          <SplashScreen />
        ) : (
          // Returning after screen lock/refresh: simple pulsing heart loader
          <PageLoader message="Подключение..." />
        ))}
      <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden">
        {resolvedContent}
      </div>
    </>
  );
}

// Main app with routes
function AppRoutes() {
  return (
    <AuthGuard>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/welcome" element={<WelcomePage />} />
        <Route
          path="/schedule"
          element={
            <ScheduleErrorBoundary>
              <SchedulePage />
            </ScheduleErrorBoundary>
          }
        />
        <Route path="/events" element={<Navigate to="/schedule" replace />} />
        <Route
          path="/event/:eventId"
          element={
            <EventsErrorBoundary>
              <EventPage />
            </EventsErrorBoundary>
          }
        />
        <Route
          path="/order/:orderId"
          element={
            <OrdersErrorBoundary>
              <OrderPage />
            </OrdersErrorBoundary>
          }
        />
        {/* Raffle public routes */}
        <Route path="/raffle/:raffleId/screen" element={<RaffleScreen />} />
        <Route path="/raffle/:raffleId" element={<RaffleViewerPage />} />
        {/* Gender Party public routes */}
        <Route path="/gender-party/:id/screen" element={<GenderPartyScreen />} />
        <Route path="/gender-party/:id" element={<GenderPartyViewerPage />} />
        <Route
          path="/orders"
          element={
            <OrdersErrorBoundary>
              <OrdersAndTicketsPage />
            </OrdersErrorBoundary>
          }
        />
        <Route path="/tickets" element={<Navigate to="/orders" replace />} />
        <Route
          path="/payment/:orderId"
          element={
            <PaymentErrorBoundary>
              <PaymentPage />
            </PaymentErrorBoundary>
          }
        />
        <Route
          path="/payment/:orderId/thanks"
          element={
            <PaymentErrorBoundary>
              <PaymentThanksPage />
            </PaymentErrorBoundary>
          }
        />
        <Route
          path="/ticket/:ticketId"
          element={
            <TicketsErrorBoundary>
              <TicketPage />
            </TicketsErrorBoundary>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminPanelRoute>
              <AdminHomePage />
            </AdminPanelRoute>
          }
        />
        <Route
          path="/admin/payments"
          element={
            <AdminOperatorRoute>
              <AdminPaymentsPage />
            </AdminOperatorRoute>
          }
        />
        <Route
          path="/admin/orders"
          element={
            <AdminOperatorRoute>
              <AdminOrdersPage />
            </AdminOperatorRoute>
          }
        />
        <Route
          path="/admin/orders/cash-issue"
          element={
            <AdminOperatorRoute>
              <AdminCashTicketsPage />
            </AdminOperatorRoute>
          }
        />
        <Route
          path="/admin/events"
          element={
            <AdminAdminRoute>
              <AdminEventsPage />
            </AdminAdminRoute>
          }
        />
        <Route
          path="/admin/events/:eventId"
          element={
            <AdminAdminRoute>
              <AdminEventEditPage />
            </AdminAdminRoute>
          }
        />
        <Route
          path="/admin/events/:eventId/orders"
          element={
            <AdminOperatorRoute>
              <EventOrdersPage />
            </AdminOperatorRoute>
          }
        />
        <Route
          path="/admin/categories"
          element={
            <AdminAdminRoute>
              <AdminCategoriesPage />
            </AdminAdminRoute>
          }
        />
        <Route
          path="/admin/categories/:categoryId"
          element={
            <AdminAdminRoute>
              <AdminCategoryEditPage />
            </AdminAdminRoute>
          }
        />
        <Route
          path="/admin/home-groups"
          element={
            <AdminAdminRoute>
              <AdminHomeGroupsPage />
            </AdminAdminRoute>
          }
        />
        <Route
          path="/admin/home-groups/:id"
          element={
            <AdminAdminRoute>
              <AdminHomeGroupEditPage />
            </AdminAdminRoute>
          }
        />
        <Route
          path="/admin/ministries"
          element={
            <AdminAdminRoute>
              <AdminMinistriesPage />
            </AdminAdminRoute>
          }
        />
        <Route
          path="/admin/ministries/:id"
          element={
            <AdminAdminRoute>
              <AdminMinistryEditPage />
            </AdminAdminRoute>
          }
        />
        {/* Raffle routes - accessible by raffle.manage or admin.full */}
        <Route
          path="/admin/raffles"
          element={
            <AdminRaffleRoute>
              <AdminRaffleEventsPage />
            </AdminRaffleRoute>
          }
        />
        <Route
          path="/admin/raffle/event/:eventId"
          element={
            <AdminRaffleRoute>
              <AdminRafflePage />
            </AdminRaffleRoute>
          }
        />
        <Route
          path="/admin/raffle/:raffleId"
          element={
            <AdminRaffleRoute>
              <AdminRaffleEditPage />
            </AdminRaffleRoute>
          }
        />
        {/* Gender Party admin routes */}
        <Route
          path="/admin/gender-party"
          element={
            <AdminGenderPartyRoute>
              <AdminGenderPartyListPage />
            </AdminGenderPartyRoute>
          }
        />
        <Route
          path="/admin/gender-party/new"
          element={
            <AdminGenderPartyRoute>
              <AdminGenderPartyEditPage />
            </AdminGenderPartyRoute>
          }
        />
        <Route
          path="/admin/gender-party/:id"
          element={
            <AdminGenderPartyRoute>
              <AdminGenderPartyEditPage />
            </AdminGenderPartyRoute>
          }
        />
        {/* Academy admin routes */}
        <Route
          path="/admin/academy"
          element={
            <AdminAcademyRoute>
              <AdminCoursesPage />
            </AdminAcademyRoute>
          }
        />
        <Route
          path="/admin/academy/new"
          element={
            <AdminAcademyRoute>
              <AdminCourseEditPage />
            </AdminAcademyRoute>
          }
        />
        <Route
          path="/admin/academy/:courseId"
          element={
            <AdminAcademyRoute>
              <AdminCourseEditPage />
            </AdminAcademyRoute>
          }
        />
        <Route
          path="/admin/academy/:courseId/analytics"
          element={
            <AdminAcademyRoute>
              <CourseAnalyticsPage />
            </AdminAcademyRoute>
          }
        />
        <Route
          path="/admin/content"
          element={
            <AdminAdminRoute>
              <AdminContentPage />
            </AdminAdminRoute>
          }
        />
        <Route
          path="/admin/content/feedback"
          element={
            <AdminAdminRoute>
              <AdminFeedbackPage />
            </AdminAdminRoute>
          }
        />
        <Route
          path="/admin/content/pastor"
          element={
            <AdminAdminRoute>
              <AdminPastorPage />
            </AdminAdminRoute>
          }
        />
        <Route
          path="/admin/content/church"
          element={
            <AdminAdminRoute>
              <AdminChurchPage />
            </AdminAdminRoute>
          }
        />
        <Route
          path="/admin/content/home-groups"
          element={
            <AdminAdminRoute>
              <AdminHomeGroupsPageConfig />
            </AdminAdminRoute>
          }
        />
        <Route
          path="/admin/content/welcome"
          element={
            <AdminAdminRoute>
              <AdminWelcomePage />
            </AdminAdminRoute>
          }
        />
        <Route
          path="/admin/tickets"
          element={
            <AdminTicketRoute>
              <AdminTicketEventsPage />
            </AdminTicketRoute>
          }
        />
        <Route
          path="/admin/tickets/validate"
          element={
            <AdminTicketRoute>
              <TicketValidationPage />
            </AdminTicketRoute>
          }
        />
        <Route
          path="/admin/tickets/cash"
          element={
            <AdminTicketIssueRoute>
              <AdminCashTicketsPage />
            </AdminTicketIssueRoute>
          }
        />
        <Route
          path="/admin/tickets/:ticketId/view"
          element={
            <AdminTicketRoute>
              <AdminTicketViewPage />
            </AdminTicketRoute>
          }
        />
        <Route
          path="/admin/access"
          element={
            <AdminAdminRoute>
              <AdminAccessPage />
            </AdminAdminRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProfileErrorBoundary>
              <ProfilePage />
            </ProfileErrorBoundary>
          }
        />
        <Route
          path="/profile/settings"
          element={
            <ProfileErrorBoundary>
              <ProfileSettingsPage />
            </ProfileErrorBoundary>
          }
        />
        <Route
          path="/profile/email"
          element={
            <ProfileErrorBoundary>
              <ProfileEmailPage />
            </ProfileErrorBoundary>
          }
        />
        <Route
          path="/profile/password"
          element={
            <ProfileErrorBoundary>
              <ProfilePasswordPage />
            </ProfileErrorBoundary>
          }
        />
        <Route path="/install" element={<InstallInstructionPage />} />
        <Route path="/auth/magic" element={<MagicLinkPage />} />
        {/* Academy routes */}
        <Route path="/academy" element={<AcademyPage />} />
        <Route path="/academy/:courseId" element={<CoursePage />} />
        {/* Legacy redirect - /university now goes to /academy */}
        <Route path="/university" element={<Navigate to="/academy" replace />} />
        <Route
          path="/prayers"
          element={
            <PrayersErrorBoundary>
              <PrayersPage />
            </PrayersErrorBoundary>
          }
        />
        <Route path="/need-prayer" element={<NeedPrayerPage />} />
        <Route path="/donations" element={<DonationsPage />} />
        <Route
          path="/church"
          element={
            <ChurchErrorBoundary>
              <ChurchPage />
            </ChurchErrorBoundary>
          }
        />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/pastor" element={<PastorPage />} />
        <Route path="/materials" element={<SoonPage title="Материалы" />} />
        <Route
          path="/worship"
          element={
            <WorshipErrorBoundary>
              <WorshipPage />
            </WorshipErrorBoundary>
          }
        />
        <Route path="/home-groups" element={<HomeGroupsPage />} />
        <Route
          path="/home-group/:id"
          element={
            <HomeGroupErrorBoundary>
              <HomeGroupPage />
            </HomeGroupErrorBoundary>
          }
        />
        <Route path="/clubs" element={<BusinessClubPage />} />
        <Route path="/courses" element={<SoonPage title="Курсы" />} />
        <Route path="/leadership-school" element={<LeadershipSchoolPage />} />
        <Route path="/womens-breakfasts" element={<WomensBreakfastsPage />} />
        <Route path="/master-classes" element={<MasterClassesPage />} />
        <Route path="/family-club" element={<FamilyClubPage />} />
        <Route path="/youth" element={<YouthPage />} />
        <Route path="/start-course" element={<StartCoursePage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AuthGuard>
  );
}

// PWA update banner: shown when a new version is available (registerType: "prompt")
function PwaUpdateBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onUpdate = () => setShow(true);
    window.addEventListener("pwa-update", onUpdate);
    return () => window.removeEventListener("pwa-update", onUpdate);
  }, []);

  const handleReload = async () => {
    // Tell the waiting SW to skip waiting and become active
    const registration = await navigator.serviceWorker?.getRegistration();
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
    }
    // Call updateSW from vite-plugin-pwa (triggers controllerchange -> reload)
    await window.__pwaUpdateServiceWorker?.();
    // Reload to use the new SW
    window.location.reload();
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] flex items-center justify-between gap-3 bg-[var(--brand-deep)] text-white px-4 py-3 safe-area-bottom shadow-lg"
      role="alert"
    >
      <span className="text-sm font-medium">Доступно обновление</span>
      <button
        type="button"
        onClick={handleReload}
        className="shrink-0 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors"
      >
        Обновить
      </button>
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary feature="Приложение" showRetry showHome>
      <UrqlProvider>
        <AuthProvider>
          <div className="h-full flex flex-col min-h-0 overflow-hidden">
            <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden">
              <AppRoutes />
            </div>
            <PwaUpdateBanner />
          </div>
        </AuthProvider>
      </UrqlProvider>
    </ErrorBoundary>
  );
}
